import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Wallet,
  Zap,
  Gift,
  Undo2,
  SlidersHorizontal,
  Timer,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Lock,
} from 'lucide-react';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import type { CreditTransactionType, BillingTransaction, CreditPackage } from '@blogforge/shared';
import { formatCredits, formatDate, formatMoney, formatSignedCredits } from '../lib/billing-format.js';

// ─────────────────────────────────────────────────────────────────
// Credits — the metered AI wallet of the active workspace: balance,
// credit package top-ups (Paystack hosted checkout) and the usage
// ledger. Distinct from the subscription plan (see PlansPage).
// ─────────────────────────────────────────────────────────────────

const LEDGER_PAGE_SIZE = 25;

const TYPE_FILTERS: Array<{ id: 'all' | CreditTransactionType; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'purchase', label: 'Purchases' },
  { id: 'usage', label: 'Usage' },
  { id: 'grant', label: 'Grants' },
  { id: 'refund', label: 'Refunds' },
  { id: 'adjustment', label: 'Adjustments' },
  { id: 'expiration', label: 'Expirations' },
];

const TYPE_META: Record<CreditTransactionType, { label: string; icon: typeof Zap; chip: string }> = {
  purchase: {
    label: 'Purchase',
    icon: ShoppingCart,
    chip: 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]',
  },
  usage: { label: 'Usage', icon: Zap, chip: 'bg-ink/5 text-ink-soft' },
  grant: {
    label: 'Grant',
    icon: Gift,
    chip: 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]',
  },
  refund: {
    label: 'Refund',
    icon: Undo2,
    chip: 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]',
  },
  adjustment: { label: 'Adjustment', icon: SlidersHorizontal, chip: 'bg-ink/5 text-ink-soft' },
  expiration: { label: 'Expiration', icon: Timer, chip: 'bg-ink/5 text-muted' },
};

function defaultDescription(tx: BillingTransaction): string {
  return `${TYPE_META[tx.type].label} — ${formatCredits(Math.abs(tx.amountMicroCredits))} credits`;
}

export function CreditsPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<'all' | CreditTransactionType>('all');

  // Switching workspace resets the ledger view.
  useEffect(() => {
    setPage(1);
    setTypeFilter('all');
  }, [activeWorkspaceId]);

  const packagesQuery = useQuery({
    queryKey: ['billing', 'packages', activeWorkspaceId],
    queryFn: () => api.billing.packages(),
    enabled: Boolean(activeWorkspaceId),
  });

  const purchaseMutation = useMutation({
    mutationFn: (packageId: string) => api.billing.purchases.create({ packageId }),
    onSuccess: (checkout) => {
      // Paystack hosted checkout — the customer returns to /billing/return.
      window.location.assign(checkout.authorizationUrl);
    },
  });

  const ledgerQuery = useQuery({
    queryKey: ['billing', 'transactions', activeWorkspaceId, page, typeFilter],
    queryFn: () =>
      api.billing.transactions({
        page,
        limit: LEDGER_PAGE_SIZE,
        ...(typeFilter === 'all' ? {} : { type: typeFilter }),
      }),
    enabled: Boolean(activeWorkspaceId),
  });

  const balanceQuery = useQuery({
    queryKey: ['billing', 'overview', activeWorkspaceId],
    queryFn: () => api.billing.overview(),
    enabled: Boolean(activeWorkspaceId),
  });

  const ledger = ledgerQuery.data;
  const balance = balanceQuery.data?.balance ?? null;
  const packages = packagesQuery.data?.packages ?? [];

  const buy = (pkg: CreditPackage) => {
    if (purchaseMutation.isPending) return;
    purchaseMutation.mutate(pkg.id);
  };

  return (
    <div>
      {/* ── Balance card ────────────────────────────────────── */}
      <div className="surface-row flex flex-wrap items-center gap-5 px-5 py-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
          <Wallet size={22} strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold tracking-wider text-muted uppercase">
            AI credit balance
          </p>
          <p className="text-2xl leading-tight font-bold tracking-tight text-ink">
            {balance ? formatCredits(balance.microCredits) : '…'}
            <span className="ml-1.5 text-[13px] font-semibold text-muted">credits</span>
          </p>
          <p className="mt-0.5 font-mono text-[10.5px] text-muted">
            {balance ? balance.microCredits.toLocaleString() : '0'} micro-credits · spent by
            metered AI usage
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/billing/invoices')}
          className="btn-ghost ml-auto px-3 py-2 text-xs"
        >
          See invoices
        </button>
      </div>

      {/* ── Top up ─────────────────────────────────────────── */}
      {packages.length > 0 && (
        <section className="mt-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-[15px] font-bold tracking-tight text-ink">Top up credits</h2>
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted">
              <Lock size={11} />
              Secure checkout powered by Paystack
            </p>
          </div>

          {purchaseMutation.error && (
            <p className="mb-3 rounded-lg border border-danger/20 bg-danger/8 px-3 py-2 text-[12.5px] text-danger">
              {purchaseMutation.error instanceof Error
                ? purchaseMutation.error.message
                : 'Could not start checkout'}
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {packages.map((pkg) => {
              const buying = purchaseMutation.isPending && purchaseMutation.variables === pkg.id;
              return (
                <div
                  key={pkg.id}
                  className="surface-row flex flex-col p-4 transition-all duration-150 hover:border-accent-border hover:shadow-glow"
                >
                  <p className="text-[10.5px] font-bold tracking-wider text-muted uppercase">
                    {pkg.name}
                  </p>
                  <p className="mt-1.5 text-[26px] leading-none font-bold tracking-tight text-ink">
                    {pkg.credits.toLocaleString()}
                    <span className="ml-1.5 text-[12px] font-semibold text-muted">credits</span>
                  </p>
                  <p className="mt-1 text-[12.5px] font-medium text-ink-soft">
                    {formatMoney(pkg.priceAmount, pkg.currency)} one-time
                  </p>
                  <button
                    type="button"
                    onClick={() => buy(pkg)}
                    disabled={purchaseMutation.isPending}
                    className="btn-primary mt-4 px-3 py-2 text-[12.5px]"
                  >
                    {buying ? 'Redirecting…' : 'Buy credits'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Usage ledger ───────────────────────────────────── */}
      <section className="mt-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-[15px] font-bold tracking-tight text-ink">
            Credit activity
            {typeof ledger?.total === 'number' && (
              <span className="ml-2 text-[12px] font-medium text-muted">
                {ledger.total.toLocaleString()} total
              </span>
            )}
          </h2>

          <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-line bg-bg/60 p-1">
            {TYPE_FILTERS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTypeFilter(t.id);
                  setPage(1);
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-all duration-150 ${
                  typeFilter === t.id
                    ? 'bg-card text-ink shadow-sm'
                    : 'text-muted hover:text-ink-soft'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {ledgerQuery.isLoading ? (
          <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted">
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
            Loading ledger…
          </div>
        ) : (ledger?.data.length ?? 0) === 0 ? (
          <div className="surface-row flex h-40 flex-col items-center justify-center text-center">
            <p className="text-sm font-semibold text-ink">No credit activity yet</p>
            <p className="mt-1 max-w-xs text-[13px] text-muted">
              Top up credits or use AI drafting to see ledger entries here.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {(ledger?.data ?? []).map((tx) => {
              const meta = TYPE_META[tx.type];
              const Icon = meta.icon;
              return (
                <div key={tx.id} className="surface-row flex items-center gap-4 px-4 py-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bg text-muted">
                    <Icon size={15} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">
                      {tx.description ?? defaultDescription(tx)}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${meta.chip}`}
                      >
                        {meta.label}
                      </span>
                      <span className="font-mono text-[11.5px] text-muted">
                        {formatDate(tx.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p
                      className={`font-mono text-[13.5px] font-semibold ${
                        tx.amountMicroCredits >= 0 ? 'text-ink' : 'text-muted'
                      }`}
                    >
                      {formatSignedCredits(tx.amountMicroCredits)}
                    </p>
                    <p className="font-mono text-[10.5px] text-muted">
                      after {formatCredits(tx.balanceAfterMicroCredits)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pager */}
        {ledger && ledger.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-[12px] text-muted">
              Page {ledger.page} of {ledger.totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={ledger.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="btn-ghost px-2.5 py-1.5 text-xs"
              >
                <ChevronLeft size={13} />
                Newer
              </button>
              <button
                type="button"
                disabled={ledger.page >= ledger.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="btn-ghost px-2.5 py-1.5 text-xs"
              >
                Older
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
